import { useEffect, useRef } from "react";

import { api } from "../api";
import { getVisitorId, markBeaconed, recentlyBeaconed } from "./viewBeacon";

const MIN_DWELL_MS = 3000;
const MIN_INTERACTIONS = 2;

/**
 * Fires the public view beacon once per page-load, only when `shortId` is set
 * (public reader passes it; the owner reader does not). Counts an "active
 * view" as >=3s dwell AND >=2 interactions. `noteInteraction` is called by the
 * reader on each page flip.
 */
export function useViewBeacon(shortId: string | undefined): { noteInteraction: () => void } {
  const interactions = useRef(0);
  const sent = useRef(false);
  const mountedAt = useRef(0);

  const maybeSend = () => {
    if (!shortId || sent.current) return;
    const now = Date.now();
    if (now - mountedAt.current < MIN_DWELL_MS) return;
    if (interactions.current < MIN_INTERACTIONS) return;
    sent.current = true;
    markBeaconed(shortId, now);
    void api.recordView(shortId, {
      visitorId: getVisitorId(),
      dwellMs: now - mountedAt.current,
      interactions: interactions.current,
    });
  };

  useEffect(() => {
    interactions.current = 0;
    sent.current = false;
    mountedAt.current = Date.now();
    if (shortId && recentlyBeaconed(shortId, mountedAt.current)) {
      sent.current = true; // refreshed within the window — don't ping the server
      return;
    }
    // Catch the case where both interactions happen before 3s of dwell.
    const t = window.setTimeout(() => maybeSend(), MIN_DWELL_MS + 50);
    return () => window.clearTimeout(t);
    // maybeSend reads only refs; intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  return {
    noteInteraction: () => {
      interactions.current += 1;
      maybeSend();
    },
  };
}
